import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { Platform } from "./PlatformSelection";
import { LANGUAGE_OPTIONS, TIMEZONE_OPTIONS } from "../constants/timezoneConstants";
import { CONTENT_TYPE_OPTIONS } from "../constants/contentTypeConstants";
import ImageView from "./ImageView";
import CanvaView from "./CanvaView";
import InputInline from "../../components/InputInline";
import { PenLine } from "lucide-react";

const reviewStyles = `
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

interface ReviewDescriptionProps {
  topic: string;
  onChangeTopic: (value) => void;
  aiSetting: any;
  selectedRange: DateRange | undefined;
  hour: number;
  minute: number;
  ampm: "AM" | "PM";
  timezone: string;
  contentType: string;
  defaultMediaId: string;
  defaultMediaType: string;
  platforms: Platform[];
  includeWeekends: boolean;
}

const ReviewDescription: React.FC<ReviewDescriptionProps> = ({
  topic,
  onChangeTopic,
  aiSetting,
  selectedRange,
  hour,
  minute,
  ampm,
  timezone,
  contentType,
  defaultMediaId,
  defaultMediaType,
  platforms,
  includeWeekends,
}) => {
  const { t } = useTranslation();

  const [isEdit, setIsEdit] = useState(false);

  const selectedPlatforms = platforms.filter((p) => p.selected);
  const platformNames = selectedPlatforms.map((p) => p.name).join(", ");
  const dateRange =
    selectedRange?.from && selectedRange?.to
      ? `${format(selectedRange.from, "MMM dd")} - ${format(selectedRange.to, "MMM dd, yyyy")}`
      : t("No date range selected");

  const timeString = `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")} ${ampm}`;
  const timezoneLabel = TIMEZONE_OPTIONS.find((opt) => opt.value === timezone)?.label || timezone;
  const contentTypeLabel =
    CONTENT_TYPE_OPTIONS.find((opt) => opt.value === contentType)?.label || contentType;

  return (
    <div className="p-4 space-y-4">
      <style>{reviewStyles}</style>

      {/* Topic Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            {t("Topic")}
          </h4>
          {isEdit ? null : (
            <button
              onClick={() => setIsEdit(true)}
              className="flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
            >
              <PenLine className="w-3 h-3 mr-1" />
              {t("Edit")}
            </button>
          )}
        </div>
        {isEdit ? (
          <InputInline
            autoFocus
            placeholder={t("Type topic...")}
            value={topic}
            onFinish={(value) => {
              onChangeTopic(value);
            }}
          />
        ) : (
          <p className="text-gray-700 text-sm leading-relaxed">{topic}</p>
        )}
      </div>

      {/* AI Profile Section */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          {t("AI Profile Information")}
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Industry")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2 line-clamp-2">
              {aiSetting?.companyIndustry || t("Not specified")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Geographic Reach")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2 line-clamp-2">
              {aiSetting?.customerLocation || t("Not specified")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Core Products/Services")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2 line-clamp-2">
              {aiSetting?.serviceDescription || t("Not specified")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Language")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2">
              {LANGUAGE_OPTIONS.find((opt) => opt.value === aiSetting?.language)?.label ||
                t("Not specified")}
            </span>
          </div>
        </div>
      </div>

      {/* Generation Settings Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
          {t("Generation Settings")}
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Schedule")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2">{dateRange}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Time")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2">
              {timeString} ({timezoneLabel})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Content Type")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2">{contentTypeLabel}</span>
          </div>
          {contentType === "image" ? (
            <div className="flex justify-end">
              <div
                className={`w-32 h-32 aspect-square overflow-hidden border border-gray-300 rounded-xl`}
              >
                {defaultMediaType === "CANVA" ? (
                  <CanvaView fileId={defaultMediaId} className="w-full h-full object-contain" />
                ) : (
                  <ImageView fileName={defaultMediaId} className="w-full h-full object-contain" />
                )}
              </div>
            </div>
          ) : null}
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Platforms")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2">
              {platformNames || t("No platforms selected")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium">{t("Include Weekends")}:</span>
            <span className="text-gray-800 text-right flex-1 ml-2">
              {includeWeekends ? t("Yes") : t("No")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDescription;
